// ASK-174 slice 1 (Client 5d) — ONE audio preference model for the hub AND the shop (coord contracts/sound-mix-fixture.json
// v1 `preference`): `kapowx_settings.audio = { master: 0–100 (70), ambient: bool (true), cues: bool (true) }`. EAGER and tiny
// (app.js reads it at boot); the engine (`_sound-engine.js`, lazy) only applies it. The hub's mute pill toggles ambient +
// cues together and writes this same key. Legacy keys are folded in ONCE and removed: the hub's `galleria.hubAudioMuted`
// '1' → ambient:false + cues:false; `masterVolume` → master (the same slider). `sfxEnabled` and `ambientEnabled` are DROPPED,
// never migrated: both defaulted false and `saveSettings()` wrote the whole object on any change (an avatar pick included), so
// a stored false was the old default, not a choice — carrying it would turn Cues OFF for every existing profile (Client 5e,
// Coord ruling on 5d 🟣 a; contract preferenceCases "legacy sfxEnabled=false present → cues=true, the legacy key removed").

export const SETTINGS_KEY = "kapowx_settings";
export const AUDIO_KEY = "audio";
export const HUB_MUTE_KEY = "galleria.hubAudioMuted";
// v3 (coord ASK-177 §13a item 3; Shaun, ALPHA031 2026-11-02: "default sounds to off"): ambient + cues default OFF; the play
// screen carries no sound chip — Settings → Audio is the one control. `audioRev` 2 marks a profile written under this rule:
// a stored audio WITHOUT it carries v1's `true`, written at first load (never a choice), so it is turned off ONCE.
export const AUDIO_DEFAULTS = Object.freeze({ master: 70, ambient: false, cues: false });
export const AUDIO_REV_KEY = "audioRev", AUDIO_REV = 2;

/** Clamp / default a stored value (never throws). */
export function normaliseAudio(a) {
	const o = a && typeof a === "object" ? a : {};
	const m = Number(o.master);
	return {
		master: Number.isFinite(m) && o.master !== null && o.master !== "" ? Math.round(Math.max(0, Math.min(100, m))) : AUDIO_DEFAULTS.master,
		ambient: typeof o.ambient === "boolean" ? o.ambient : AUDIO_DEFAULTS.ambient,
		cues: typeof o.cues === "boolean" ? o.cues : AUDIO_DEFAULTS.cues,
	};
}

const readJson = (s, k) => { try { return JSON.parse(s?.getItem?.(k) || "null"); } catch { return null; } };

/** → { audio, settings, migrated[] }: the model in force, with any legacy key folded in once (and written back). */
export function loadAudioPrefs(storage) {
	const migrated = [];
	let settings = readJson(storage, SETTINGS_KEY);
	if (!settings || typeof settings !== "object" || Array.isArray(settings)) settings = {};
	let audio = settings[AUDIO_KEY] && typeof settings[AUDIO_KEY] === "object" ? { ...settings[AUDIO_KEY] } : null;
	const fresh = !audio;
	if (fresh) audio = { ...AUDIO_DEFAULTS };
	if ("masterVolume" in settings) { if (fresh) audio.master = settings.masterVolume; delete settings.masterVolume; migrated.push("masterVolume"); }
	if ("sfxEnabled" in settings) { delete settings.sfxEnabled; migrated.push("sfxEnabled"); }   // dead: removed, never read (cues keeps its value / default)
	if ("ambientEnabled" in settings) { delete settings.ambientEnabled; migrated.push("ambientEnabled"); }
	let hubMute = null;
	try { hubMute = storage?.getItem?.(HUB_MUTE_KEY) ?? null; } catch { hubMute = null; }
	if (hubMute !== null) {
		if (hubMute === "1") { audio.ambient = false; audio.cues = false; }
		try { storage.removeItem(HUB_MUTE_KEY); } catch { /* blocked */ }
		migrated.push("hubMuteKey");
	}
	if (!fresh && settings[AUDIO_REV_KEY] !== AUDIO_REV) { audio.ambient = false; audio.cues = false; migrated.push("rev"); }
	audio = normaliseAudio(audio);
	if (migrated.length || fresh) {
		settings[AUDIO_KEY] = audio;
		settings[AUDIO_REV_KEY] = AUDIO_REV;
		try { storage?.setItem?.(SETTINGS_KEY, JSON.stringify(settings)); } catch { /* blocked storage: the model still applies this session */ }
	}
	return { audio, settings, migrated };
}

/** Write the model into kapowx_settings (the rest of the object kept). → true when stored. */
export function saveAudioPrefs(storage, audio) {
	try {
		const s = readJson(storage, SETTINGS_KEY);
		const settings = s && typeof s === "object" && !Array.isArray(s) ? s : {};
		settings[AUDIO_KEY] = normaliseAudio(audio);
		settings[AUDIO_REV_KEY] = AUDIO_REV;
		storage.setItem(SETTINGS_KEY, JSON.stringify(settings));
		return true;
	} catch { return false; }
}

export const sliderValue = (a) => normaliseAudio(a).master;

/** The engine's three multipliers: master on the master bus, ambient on bed + spots, cues on ui + diegetic. */
export function busGains(a) {
	const n = normaliseAudio(a);
	return { master: n.master / 100, ambient: n.ambient ? 1 : 0, cues: n.cues ? 1 : 0 };
}
